import React from 'react'
import { useLanguageStore } from '../../store/languageStore'
import type { LanguageCode } from '../../../../shared/types'

interface PronunciationFeedbackProps {
  feedback: string
  confidence: number
  onClose?: () => void
}

const getToneStyles = (score: number) => {
  if (score >= 0.9) {
    return {
      badge: 'bg-success-50 text-success-700 border-success-200',
      bar: 'bg-success-500'
    }
  }

  if (score >= 0.7) {
    return {
      badge: 'bg-warning-50 text-warning-700 border-warning-200',
      bar: 'bg-warning-500'
    }
  }

  return {
    badge: 'bg-error-50 text-error-700 border-error-200',
    bar: 'bg-error-500'
  }
}

export function PronunciationFeedback({
  feedback,
  confidence,
  onClose,
}: PronunciationFeedbackProps) {
  const currentLanguage = useLanguageStore((state) => state.currentLanguage)
  const tone = getToneStyles(confidence)

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg p-5 animate-slide-down">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">dY'�</span>
            <div>
              <p className="text-sm uppercase tracking-wide text-[var(--color-text-secondary)]">
                {getTitle(currentLanguage)}
              </p>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold ${tone.badge}`}>
                <span>
                  {Math.round(confidence * 100)}%
                </span>
                <span>
                  {getConfidenceLabel(confidence, currentLanguage)}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              <span>{getConfidenceText(currentLanguage)}</span>
              <span className="font-semibold text-[var(--color-text)]">
                {Math.round(confidence * 100)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-[var(--color-border)]/60 overflow-hidden">
              <div
                className={`h-full ${tone.bar}`}
                style={{ width: `${confidence * 100}%` }}
              />
            </div>
          </div>

          <p className="text-sm text-[var(--color-text)] whitespace-pre-wrap leading-relaxed">
            {feedback}
          </p>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
            aria-label="Close pronunciation feedback"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

const getConfidenceLabel = (score: number, languageCode: LanguageCode) => {
  const labels: Record<LanguageCode, { excellent: string; good: string; fair: string; needsImprovement: string }> = {
    ja: { excellent: 'a?Ta?�a,%a?-a?,', good: '�%_a?,', fair: 'a?_a?,a?_a?,', needsImprovement: '��?�"1�-,' },
    es: { excellent: 'Excelente', good: 'Bueno', fair: 'Aceptable', needsImprovement: 'Necesita mejorar' },
    tl: { excellent: 'Napakahusay', good: 'Mabuti', fair: 'Katamtaman', needsImprovement: 'Kailangan pang pagbutihin' },
    fr: { excellent: 'Excellent', good: 'Bon', fair: 'Passable', needsImprovement: 'A améliorer' },
    en: { excellent: 'Excellent', good: 'Good', fair: 'Fair', needsImprovement: 'Needs Improvement' },
    zh: { excellent: '优秀', good: '良好', fair: '尚可', needsImprovement: '需要改进' },
    it: { excellent: 'Eccellente', good: 'Buono', fair: 'Discreto', needsImprovement: 'Da migliorare' },
    de: { excellent: 'Ausgezeichnet', good: 'Gut', fair: 'Befriedigend', needsImprovement: 'Verbesserungsbedarf' },
    to: { excellent: 'Lelei \'aupito', good: 'Lelei', fair: 'Sai pe', needsImprovement: '\'Oku fiema\'u ke fakalelei ange' },
    sm: { excellent: 'Sili ona lelei', good: 'Lelei', fair: 'Fetaui', needsImprovement: 'E mana\'omia le fa\'aleleia' },
    pt: { excellent: 'Excelente', good: 'Bom', fair: 'Razoável', needsImprovement: 'Precisa melhorar' },
    hi: { excellent: 'उत्कृष्ट', good: 'अच्छा', fair: 'ठीक', needsImprovement: 'सुधार की आवश्यकता' },
  }

  const langLabels = labels[languageCode] || labels.en

  if (score >= 0.9) return langLabels.excellent
  if (score >= 0.8) return langLabels.good
  if (score >= 0.7) return langLabels.fair
  return langLabels.needsImprovement
}

const getTitle = (languageCode: LanguageCode) => {
  const titles: Record<LanguageCode, string> = {
    ja: '発音フィードバック',
    es: 'Retroalimentación de pronunciación',
    tl: 'Feedback sa Pagbigkas',
    fr: 'Retour sur la prononciation',
    en: 'Pronunciation feedback',
    zh: '发音反馈',
    it: 'Feedback sulla pronuncia',
    de: 'Aussprache-Feedback',
    to: 'Fakamatala ki he lea',
    sm: 'Manatu e uiga i le tautala',
    pt: 'Feedback de pronúncia',
    hi: 'उच्चारण प्रतिक्रिया',
  }
  return titles[languageCode] || titles.en
}

const getConfidenceText = (languageCode: LanguageCode) => {
  const texts: Record<LanguageCode, string> = {
    ja: '自信度',
    es: 'Confianza',
    tl: 'Tiwala',
    fr: 'Confiance',
    en: 'Confidence',
    zh: '信心',
    it: 'Sicurezza',
    de: 'Vertrauen',
    to: 'Tui',
    sm: 'Faatuatua',
    pt: 'Confiança',
    hi: 'विश्वास',
  }
  return texts[languageCode] || texts.en
}
